export { type FileStoragePathSegment } from '../api/fileStorageApi';

export const EMPTY_FOLDER_PARENT_ID = '00000000-0000-0000-0000-000000000000';

export const isRootFolderParentId = (parentId: string | null | undefined): boolean =>
  !parentId || parentId === EMPTY_FOLDER_PARENT_ID;

const ensureLeadingSlash = (value: string) => (value.startsWith('/') ? value : `/${value}`);

export const normalizePathname = (value: string) => {
  if (!value) return '/';
  const normalized = ensureLeadingSlash(value);
  return normalized !== '/' && normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
};

export const encodePathSegment = (segment: string) =>
  encodeURIComponent(segment.trim().toLowerCase());

const FILE_SELECTION_PATTERN =
  /^(?<id>[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-(?<name>.+)$/i;

const safeDecodeSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const parseFileSelectionSegment = (segment: string) => {
  const trimmedSegment = segment.trim();
  if (!trimmedSegment) return null;

  const decodedSegment = safeDecodeSegment(trimmedSegment);
  const match = decodedSegment.match(FILE_SELECTION_PATTERN);
  if (!match?.groups?.id || !match.groups.name) return null;
  return { id: match.groups.id, slug: match.groups.name };
};

const encodeFileSlug = (value: string) => encodeURIComponent(value.trim().toLowerCase());

export const parseFileStoragePath = (pathname: string) => {
  const segments = normalizePathname(pathname).split('/').filter(Boolean);
  const workspaceFilesIndex = segments.findIndex(
    (segment, index) => segment === 'workspace' && segments[index + 1] === 'files'
  );

  if (workspaceFilesIndex === -1) {
    return { basePath: normalizePathname(pathname), folderSegments: [] as string[] };
  }

  const rootEndIndex = workspaceFilesIndex + 3;
  if (!segments[rootEndIndex - 1]) {
    return { basePath: normalizePathname(pathname), folderSegments: [] as string[] };
  }

  const basePath = `/${segments.slice(0, rootEndIndex).join('/')}`;
  const pathSegments = segments.slice(rootEndIndex);
  const maybeFileSelection =
    pathSegments.length > 0
      ? parseFileSelectionSegment(pathSegments[pathSegments.length - 1])
      : null;
  const folderSegments = pathSegments
    .slice(0, maybeFileSelection ? -1 : undefined)
    .map((segment) => safeDecodeSegment(segment))
    .filter(Boolean);

  return { basePath, folderSegments, selectedFile: maybeFileSelection };
};

export const buildFileStoragePath = (
  basePath: string,
  folderSegments: string[],
  selectedFile?: { id: string; name: string } | null
) => {
  const normalizedBasePath = normalizePathname(basePath);
  const encodedSegments = folderSegments.map((segment) => encodePathSegment(segment));
  const encodedFileSelection =
    selectedFile?.id && selectedFile?.name
      ? `${encodeURIComponent(selectedFile.id)}-${encodeFileSlug(selectedFile.name)}`
      : null;
  const allSegments = encodedFileSelection
    ? [...encodedSegments, encodedFileSelection]
    : encodedSegments;
  if (!allSegments.length) return normalizedBasePath;
  return `${normalizedBasePath}/${allSegments.join('/')}`;
};
